/**
 * Public webinar API (no auth except claim-registration).
 *
 * Backend contract:
 * - GET  /api/webinars/public/{slug}
 * - POST /api/webinars/public/{slug}/register  body: WebinarPublicRegisterRequest
 * - POST /api/webinars/claim-registration      body: { link_token }  Authorization: Bearer
 * - GET  /api/admin/webinars                   admin only
 */
import { API_BASE_URL, getAuthHeaders } from '../../utils/utils';

const CLAIM_STORAGE_KEY = 'webinar_claim_token';
const REGISTERED_SLUGS_STORAGE_KEY = 'registered_public_webinar_slugs';

export const setPendingWebinarClaimToken = (linkToken) => {
  if (linkToken) {
    sessionStorage.setItem(CLAIM_STORAGE_KEY, linkToken);
  }
};

export const getPendingWebinarClaimToken = () =>
  sessionStorage.getItem(CLAIM_STORAGE_KEY);

export const clearPendingWebinarClaimToken = () => {
  sessionStorage.removeItem(CLAIM_STORAGE_KEY);
};

const readRegisteredPublicWebinarSlugs = () => {
  try {
    return JSON.parse(
      localStorage.getItem(REGISTERED_SLUGS_STORAGE_KEY) || '[]'
    );
  } catch {
    return [];
  }
};

export const markPublicWebinarRegistered = (slug) => {
  if (!slug) return;
  const current = readRegisteredPublicWebinarSlugs();
  if (current.includes(slug)) return;
  try {
    localStorage.setItem(
      REGISTERED_SLUGS_STORAGE_KEY,
      JSON.stringify([...current, slug])
    );
  } catch {
    // Ignore storage failures; registration still succeeded server-side.
  }
};

export const hasRegisteredPublicWebinar = (slug) => {
  if (!slug) return false;
  return readRegisteredPublicWebinarSlugs().includes(slug);
};

/**
 * After login/signup, link the Liwaza account to the public webinar registration.
 */
export async function claimWebinarRegistrationIfPending() {
  const linkToken = getPendingWebinarClaimToken();
  if (!linkToken) {
    return { ok: true, skipped: true };
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/webinars/claim-registration`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ link_token: linkToken }),
      }
    );
    if (response.ok) {
      clearPendingWebinarClaimToken();
      return { ok: true, skipped: false };
    }
    const err = await response.json().catch(() => ({}));
    return {
      ok: false,
      skipped: false,
      detail: err.detail || err.message || 'Claim failed',
    };
  } catch (e) {
    return { ok: false, skipped: false, detail: e.message };
  }
}

export async function fetchPublicWebinar(slug) {
  const response = await fetch(
    `${API_BASE_URL}/api/webinars/public/${encodeURIComponent(slug)}`
  );
  const data = await response.json().catch(() => ({}));
  if (response.status === 410) {
    const err = new Error(
      typeof data.detail === 'string'
        ? data.detail
        : 'This event is no longer available for registration.'
    );
    err.code = data.code || 'webinar_unavailable';
    err.status = 410;
    throw err;
  }
  if (!response.ok) {
    throw new Error(
      typeof data.detail === 'string'
        ? data.detail
        : 'Webinar not found.'
    );
  }
  return data;
}

export async function registerPublicWebinar(slug, payload) {
  const response = await fetch(
    `${API_BASE_URL}/api/webinars/public/${encodeURIComponent(slug)}/register`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (response.status === 410) {
    const err = new Error(
      typeof data.detail === 'string'
        ? data.detail
        : 'This event is no longer available for registration.'
    );
    err.code = data.code || 'webinar_unavailable';
    err.status = 410;
    throw err;
  }
  if (!response.ok) {
    throw new Error(
      typeof data.detail === 'string'
        ? data.detail
        : data.detail?.[0]?.msg || 'Registration failed.'
    );
  }
  return data;
}
